import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search, MapPin, ExternalLink, Instagram } from 'lucide-react';
import SocialLinks, { SOCIAL_FIELDS, countSocialLinks, normalizeSocialUrl } from './SocialLinks';
import { motion } from 'framer-motion';
// CategoryNav removed
import type { PlaceResult } from '../hooks/useGooglePlaces';
import { useManagedCollection, slugify } from '../admin/useManagedCollection';
import { AdminItem, AdminAddBar } from '../admin/AdminControls';
import type { FieldDef } from '../admin/AdminFieldModal';
import NewsTicker from './NewsTicker';

const DESSERT_FIELDS: FieldDef[] = [
  { key: 'name', label: 'Name', required: true },
  { key: 'location', label: 'Location', placeholder: 'Frisco' },
  { key: 'city', label: 'City', placeholder: 'Frisco' },
  { key: 'address', label: 'Address', type: 'textarea' },
  { key: 'image', label: 'Image URL', type: 'image', placeholder: 'https://…' },
  { key: 'mapsUrl', label: 'Google Maps URL', type: 'url' },
  ...SOCIAL_FIELDS,
];

// InstagramWidget removed — only on home page

// Admin-added custom desserts only carry the DESSERT_FIELDS keys, so `cuisines`
// is undefined on them (12 such rows exist today) — and the platform stores a
// saved multiselect as a comma-joined string rather than an array. Both shapes
// have to be normalized here: DESSERT_TYPES currently lists only "Home Bakers"
// (which takes the curated-id branch), but the moment a second type chip is
// added, a bare `r.cuisines.includes(...)` would throw on every custom card.
function toTypeArray(v: unknown): string[] {
  if (Array.isArray(v)) return v.map((x) => String(x).trim()).filter(Boolean);
  if (typeof v === "string") return v.split(",").map((x) => x.trim()).filter(Boolean);
  return [];
}

const DESSERT_TYPES = [
  { name: 'Home Bakers', icon: '🧁' },
  // 'Cafe' is a cross-link, not a filter: clicking it opens the dedicated
  // Cafe category page (/cafe) instead of filtering this list.
  { name: 'Cafe', icon: '☕', href: '/cafe' },
];

const HERO_LOCATIONS = ['All Locations', 'Frisco', 'Plano', 'Irving', 'Lewisville', 'McKinney', 'Dallas', 'Carrollton', 'The Colony', 'Allen', 'Prosper', 'Aubrey', 'Little Elm', 'Melissa', 'FlowerMound', 'Richardson', 'Addison', 'Online', 'Other'];

// Every dessert-shop listing's public detail-page URL is derived from its stable
// id (dessert-1, dessert-2, ...) or, for admin-added cards with no preset id,
// its name — same pattern as templeSlug in Temples.tsx. Used by both the card
// link and DessertDetailPage's lookup.
export function dessertSlug(d: { id?: string; name?: string }) {
  return slugify(d.id || d.name || '');
}

export const DESSERTS_DATA: (PlaceResult & { instagramUrl?: string; imageFit?: 'cover' | 'contain' })[] = [
  {
    id: 'dessert-1',
    name: 'Thick Shake Factory',
    location: 'Irving',
    city: 'Irving',
    address: '10045 N MacArthur Blvd #113, Irving, TX 75063, United States',
    rating: 4.3,
    reviewCount: 0,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783000130192-8f0lxm.png',
    placeId: 'dessert-1',
    mapsUrl: 'https://www.google.com/maps/search/Thick+Shake+Factory+10045+N+MacArthur+Blvd+Irving+TX+75063',
    cuisines: ['Shakes', 'Home Bakers'],
  },
  {
    id: 'dessert-2',
    name: 'Naturals Rolls & Ice Cream',
    location: 'Frisco',
    city: 'Frisco',
    address: '5266 Independence Pkwy, Suite 105, Frisco, TX 75035',
    rating: 4.4,
    reviewCount: 0,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783456064101-o47gy.png',
    placeId: 'dessert-2',
    mapsUrl: 'https://www.google.com/maps/search/Naturals+Rolls+Ice+Cream+5266+Independence+Pkwy+Frisco+TX+75035',
    cuisines: ['Home Bakers'],
  },
  {
    id: 'dessert-3',
    name: 'Maya Creamery',
    location: 'Irving',
    city: 'Irving',
    address: '3351 Regent Blvd, Ste 110, Irving, TX 75063',
    rating: 4.5,
    reviewCount: 0,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783624500373-kc0a8i.png',
    placeId: 'dessert-3',
    mapsUrl: 'https://www.google.com/maps/search/Maya+Creamery+3351+Regent+Blvd+Irving+TX+75063',
    cuisines: ['Home Bakers', 'Indian Sweets'],
  },
  {
    id: 'dessert-4',
    name: 'Maya Creamery',
    location: 'FlowerMound',
    city: 'FlowerMound',
    address: '1260 Flower Mound Rd, Ste 100, Flower Mound, TX 75028',
    rating: 4.5,
    reviewCount: 0,
    image: 'https://images.unsplash.com/photo-1563805042-7684c019e1cb?w=600&h=400&fit=crop',
    placeId: 'dessert-4',
    mapsUrl: 'https://www.google.com/maps/search/Maya+Creamery+1260+Flower+Mound+Rd+Flower+Mound+TX+75028',
    cuisines: ['Home Bakers', 'Indian Sweets'],
  },
  {
    id: 'dessert-5',
    name: 'Cremeverse',
    location: 'Irving',
    city: 'Irving',
    address: '3341 Regent Blvd, Ste 140, Irving, TX 75063',
    rating: 4.2,
    reviewCount: 0,
    image: 'https://images.unsplash.com/photo-1551024506-0bccd828d307?w=600&h=400&fit=crop',
    placeId: 'dessert-5',
    mapsUrl: 'https://www.google.com/maps/search/Cremeverse+3341+Regent+Blvd+Irving+TX+75063',
    cuisines: ['Home Bakers', 'Dessert Cafes'],
  },
  {
    id: 'dessert-6',
    name: 'Maya Creamery',
    location: 'McKinney',
    city: 'McKinney',
    address: '7650 Stacy Rd, Unit 210, McKinney, TX 75070',
    rating: 4.5,
    reviewCount: 0,
    image: 'https://images.unsplash.com/photo-1563805042-7684c019e1cb?w=600&h=400&fit=crop',
    placeId: 'dessert-6',
    mapsUrl: 'https://www.google.com/maps/search/Maya+Creamery+7650+Stacy+Rd+McKinney+TX+75070',
    cuisines: ['Home Bakers', 'Indian Sweets'],
  },
  {
    id: 'dessert-7',
    name: 'Maya Creamery',
    location: 'Little Elm',
    city: 'Little Elm',
    address: '11700 FM 423, Ste 230, Little Elm, TX 75068',
    rating: 4.5,
    reviewCount: 0,
    image: 'https://images.unsplash.com/photo-1563805042-7684c019e1cb?w=600&h=400&fit=crop',
    placeId: 'dessert-7',
    mapsUrl: 'https://www.google.com/maps/search/Maya+Creamery+11700+FM+423+Little+Elm+TX+75068',
    cuisines: ['Home Bakers', 'Indian Sweets'],
  },
  {
    id: 'dessert-8',
    name: 'Coco Mango',
    location: 'McKinney',
    city: 'McKinney',
    address: '3540 W University Dr STE 300, McKinney, TX 75071',
    rating: 4.3,
    reviewCount: 0,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783572742089-gsjgq.png',
    placeId: 'dessert-8',
    mapsUrl: 'https://www.google.com/maps/search/Coco+Mango+3540+W+University+Dr+McKinney+TX+75071',
    cuisines: ['Home Bakers', 'Shakes'],
  },
  {
    id: 'dessert-9',
    name: 'Gold Krunch',
    location: 'Online',
    city: 'Online',
    address: '@gold_krunch',
    rating: 0,
    reviewCount: 0,
    image: 'https://images.unsplash.com/photo-1551024601-bec78aea704b?w=600&h=400&fit=crop',
    placeId: 'dessert-9',
    mapsUrl: '',
    cuisines: ['Dessert Cafes'],
    instagramUrl: 'https://www.instagram.com/gold_krunch/',
  },
  {
    id: 'dessert-10',
    name: "Aani's Home Bakery",
    location: 'Online',
    city: 'Online',
    address: '@aanis.home.bakery',
    rating: 0,
    reviewCount: 0,
    image: 'https://images.unsplash.com/photo-1551024601-bec78aea704b?w=600&h=400&fit=crop',
    placeId: 'dessert-10',
    mapsUrl: '',
    cuisines: ['Home Bakers'],
    instagramUrl: 'https://www.instagram.com/aanis.home.bakery/',
  },
  {
    id: 'dessert-11',
    name: 'Dream Your Cakes',
    location: 'Online',
    city: 'Online',
    address: '@dreamyourcakes',
    rating: 0,
    reviewCount: 0,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783572246360-x2jcgg.png',
    placeId: 'dessert-11',
    mapsUrl: '',
    cuisines: ['Cakes & Pastries'],
    instagramUrl: 'https://www.instagram.com/dreamyourcakes/',
  },
  {
    id: 'dessert-12',
    name: 'Color Drama Cakes',
    location: 'Online',
    city: 'Online',
    address: '@color_drama_cakes',
    rating: 0,
    reviewCount: 0,
    image: 'https://images.unsplash.com/photo-1551024601-bec78aea704b?w=600&h=400&fit=crop',
    placeId: 'dessert-12',
    mapsUrl: '',
    cuisines: ['Home Bakers'],
    instagramUrl: 'https://www.instagram.com/color_drama_cakes/',
  },
  {
    id: 'dessert-13',
    name: 'Divine Treats By Pooja',
    location: 'Online',
    city: 'Online',
    address: '@divinetreatsbypooja',
    rating: 0,
    reviewCount: 0,
    image: 'https://images.unsplash.com/photo-1551024601-bec78aea704b?w=600&h=400&fit=crop',
    placeId: 'dessert-13',
    mapsUrl: '',
    cuisines: ['Home Bakers'],
    instagramUrl: 'https://www.instagram.com/divinetreatsbypooja',
  },
  {
    id: 'dessert-14',
    name: 'Drizzles Ice Cream & Waffles Aubrey',
    location: 'Aubrey',
    city: 'Aubrey',
    address: '1733 FM1385 Ste 102, Aubrey, TX 76227',
    rating: 0,
    reviewCount: 0,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783469892998-zewpi6.png',
    placeId: 'dessert-14',
    mapsUrl: 'https://www.google.com/maps/search/Drizzles+Ice+Cream+%26+Waffles+1733+FM1385+Ste+102+Aubrey+TX+76227',
    cuisines: ['Home Bakers'],
  },
  {
    id: 'dessert-15',
    name: 'Kelvin Scale Absolute Icecream',
    location: 'Frisco',
    city: 'Frisco',
    address: '2650 King Road #750, Frisco, TX 75010',
    rating: 0,
    reviewCount: 0,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783571062597-wfdh4.png',
    placeId: 'dessert-15',
    mapsUrl: 'https://www.google.com/maps/search/Kelvin+Scale+Absolute+Icecream+2650+King+Road+%23750+Frisco+TX+75010',
    cuisines: ['Home Bakers'],
    imageFit: 'contain',
  },
  {
    id: 'dessert-16',
    name: 'Mumitas Bakery',
    location: 'Aubrey',
    city: 'Aubrey',
    address: '1518 Navo Rd Ste A6, Aubrey, TX 76227',
    rating: 0,
    reviewCount: 0,
    image: 'https://images.unsplash.com/photo-1551024601-bec78aea704b?w=600&h=400&fit=crop',
    placeId: 'dessert-16',
    mapsUrl: 'https://www.google.com/maps/search/Mumitas+Bakery+1518+Navo+Rd+Ste+A6+Aubrey+TX+76227',
    cuisines: ['Home Bakers'],
    instagramUrl: 'https://www.instagram.com/mumitas_bakery/',
  },
];

// Featured by id (not name) — 'Maya Creamery' - Little Elm (dessert-7) and
// 'Maya Creamery' - McKinney (dessert-6) are intentionally excluded; they
// are not Featured by TastebudsByAnubhi.
export const FEATURED_DESSERT_IDS = new Set([
  'dessert-1', // Thick Shake Factory
  'dessert-2', // Naturals Rolls & Ice Cream
  'dessert-3', // Maya Creamery - Irving
  'dessert-4', // Maya Creamery - FlowerMound
  'dessert-5', // Cremeverse
  'dessert-8', // Coco Mango
]);

// When the 'Home Bakers' type is selected, only this curated set of
// vendors should display — not every item tagged with the Home Bakers cuisine.
const HOME_BAKERS_CURATED_IDS = new Set([
  'dessert-10', // Aani's Home Bakery
  'dessert-9',  // Gold Krunch
  'dessert-11', // Dream Your Cakes
  'dessert-12', // Color Drama Cakes
  'dessert-13', // Divine Treats By Pooja
]);

// Admin-added vendors (no stable code-default id, so matched by name) that
// should ALSO show under the 'Home Bakers' type filter.
const HOME_BAKERS_EXTRA_NAMES = new Set([
  'a2z cake couture',
  'the icing world',
]);

// These admin-added cards show only their social links, not the small
// "View Location" button — per request.
const HIDE_LOCATION_BUTTON_NAMES = new Set([
  'a2z cake couture',
  'the icing world',
]);

// Plain-text city/state label shown under the name for admin-added cards
// whose address row is hidden (see HIDE_LOCATION_BUTTON_NAMES above).
const CITY_LABEL_OVERRIDES: Record<string, string> = {
  'a2z cake couture': 'Plano, TX',
};

// Admin-added cards often only fill in the Address field and leave the
// Google Maps URL blank. Auto-derive a maps search link from the address
// so the "View Location" button always appears once an address is set
// (skip handle-style addresses like "@some_handle").
function resolveMapsUrl(dessert: PlaceResult) {
  if (dessert.mapsUrl && dessert.mapsUrl.trim()) return dessert.mapsUrl;
  const address = (dessert as any).address as string | undefined;
  if (address && address.trim() && !address.trim().startsWith('@')) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address.trim())}`;
  }
  return '';
}

export function DessertCard({ dessert, index, isFeatured }: { dessert: PlaceResult & { instagramUrl?: string }; index: number; isFeatured?: boolean }) {
  // Instagram can arrive two ways: the legacy static `instagramUrl` field (curated
  // dessert-* rows), or the admin-added SOCIAL_FIELDS `instagram` key. Resolve both
  // so every card with an Instagram link — featured or not — gets the button below.
  const resolvedInstagramUrl =
    normalizeSocialUrl(dessert.instagramUrl, "instagram") ||
    normalizeSocialUrl((dessert as any).instagram, "instagram") ||
    normalizeSocialUrl((dessert as any).instagram_url, "instagram");
  const hasInstagram = !!resolvedInstagramUrl;
  const resolvedMapsUrl = resolveMapsUrl(dessert);
  const href = hasInstagram ? resolvedInstagramUrl : resolvedMapsUrl;
  const hasAnySocial = countSocialLinks(dessert) > 0;
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      className="bg-white rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col"
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      <Link to={"/dessert-shops/" + dessertSlug(dessert)} className="flex flex-col flex-1">
        <div className="w-full h-[110px] relative overflow-hidden bg-gray-100">
          <img
            src={dessert.image}
            alt={dessert.name}
            className={(dessert as any).imageFit === 'cover' ? 'w-full h-full object-cover' : 'w-full h-full object-contain'}
            loading="lazy"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        </div>
        <div className="p-2.5 pb-1 flex-1">
          <h3 className="text-[13px] font-bold text-gray-900 leading-tight mb-0.5 line-clamp-2">{dessert.name}</h3>
          {!HIDE_LOCATION_BUTTON_NAMES.has((dessert.name || '').toLowerCase()) ? (
            !!(dessert.address && dessert.address.trim()) && (
              <div className="flex items-start gap-1">
                <MapPin className="w-2.5 h-2.5 text-pink-500 shrink-0 mt-0.5" />
                <span className="text-[10px] text-gray-500 font-medium leading-snug line-clamp-2">{dessert.address}</span>
              </div>
            )
          ) : (
            CITY_LABEL_OVERRIDES[(dessert.name || '').toLowerCase()] && (
              <div className="flex items-start gap-1">
                <MapPin className="w-2.5 h-2.5 text-pink-500 shrink-0 mt-0.5" />
                <span className="text-[10px] text-gray-500 font-medium leading-snug">{CITY_LABEL_OVERRIDES[(dessert.name || '').toLowerCase()]}</span>
              </div>
            )
          )}
          {/* Featured badge */}
          {isFeatured && (
            <div className="pt-1 flex items-center gap-1.5">
              <p className="text-[10px] font-semibold text-green-600 tracking-wide">
                Featured by TastebudsByAnubhi
              </p>
              <SocialLinks item={dessert} px={20} />
            </div>
          )}
        </div>
      </Link>
      <div className="px-2.5 pb-2.5 pt-1 flex items-center gap-1.5">
        {hasInstagram && !isFeatured && (
          <a
            href={resolvedInstagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="flex-1 flex items-center justify-center gap-1 bg-gradient-to-br from-pink-500 to-orange-400 hover:opacity-90 text-white text-[10px] font-semibold py-1.5 rounded-md transition-colors duration-200"
          >
            <Instagram className="w-2.5 h-2.5" />
            Instagram
          </a>
        )}
        {!!resolvedMapsUrl && !HIDE_LOCATION_BUTTON_NAMES.has((dessert.name || '').toLowerCase()) && (
          <a
            href={resolvedMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center gap-1 bg-[#51ba4f] hover:bg-[#43a041] text-white text-[10px] font-semibold py-1.5 rounded-md transition-colors duration-200"
          >
            <ExternalLink className="w-2.5 h-2.5" />
            View Location
          </a>
        )}
      </div>
    </motion.div>
  );
}

export default function Desserts() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedHeroLocation, setSelectedHeroLocation] = useState('All Locations');
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownSearch, setDropdownSearch] = useState('');
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [isDescExpanded, setIsDescExpanded] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownInputRef = useRef<HTMLInputElement>(null);

  const mc = useManagedCollection<any>('desserts', DESSERTS_DATA, (d, i) => slugify(d.id || d.name || String(i)));

  const effectiveLocation = selectedLocation ?? (selectedHeroLocation !== 'All Locations' ? selectedHeroLocation : null);
  const displayLocation = effectiveLocation ?? 'Dallas-DFW';

  // Filter static data by location
  const places = effectiveLocation
    ? mc.items.filter(d => d.location === effectiveLocation)
    : mc.items;

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  const filteredDesserts = places.filter((r) => {
    const matchesQuery = searchQuery.trim() === '' || r.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = !selectedType
      ? true
      : selectedType === 'Home Bakers'
        ? HOME_BAKERS_CURATED_IDS.has(r.id) || HOME_BAKERS_EXTRA_NAMES.has((r.name || '').toLowerCase())
        : toTypeArray(r.cuisines).includes(selectedType);
    const matchesLocation = !effectiveLocation || r.location === effectiveLocation;
    return matchesQuery && matchesType && matchesLocation;
  });

  const hasActiveFilters = selectedType || effectiveLocation;

  const featuredDesserts = filteredDesserts.filter((d) => FEATURED_DESSERT_IDS.has(d.id));
  const nonFeaturedDesserts = filteredDesserts.filter((d) => !FEATURED_DESSERT_IDS.has(d.id));

  return (
    <div className="min-h-screen bg-white">
      <style>{`
        .filter-scroll::-webkit-scrollbar { display: none; }
        .filter-scroll { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {/* Hero */}
      <section className="relative w-full" style={{ minHeight: "160px" }}>
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1781335778473-d0pw6i.png"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/45" />
          <svg viewBox="0 0 1440 24" preserveAspectRatio="none" style={{ position: "absolute", left: 0, bottom: "-1px", width: "100%", height: "18px", display: "block" }}>
            <path d="M0,0 Q720,24 1440,0 L1440,24 L0,24 Z" fill="#ffffff" />
          </svg>
        </div>
        <div className="relative max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 flex items-center justify-center" style={{ zIndex: 10, minHeight: "160px" }}>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} className="w-full max-w-[740px]">
            <div className="rounded-md" style={{ boxShadow: '0 0 40px 12px rgba(255,255,255,0.45), 0 0 80px 24px rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)' }}>
              <div className="flex flex-col sm:flex-row bg-white rounded-md shadow-md h-auto sm:h-[40px]">
                <div className="flex-1 px-3 py-2 sm:py-0 flex items-center border-b sm:border-b-0 sm:border-r border-gray-200 min-h-[40px]">
                  <span className="text-[11px] text-gray-600 font-semibold shrink-0 mr-2 hidden sm:inline">which restaurant?</span>
                  <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="e.g. Thick Shakes, Home Bakers, Gulab Jamun" className="w-full text-[12px] text-gray-700 placeholder:text-gray-400 outline-none bg-transparent" />
                </div>
                <div ref={dropdownRef} className="relative border-b sm:border-b-0 sm:border-r border-gray-200 min-h-[40px] sm:w-[170px] md:w-[190px] shrink-0" style={{ zIndex: 99999 }}>
                  <button onClick={() => setShowDropdown(!showDropdown)} className="w-full h-full px-3 py-2 sm:py-0 flex items-center gap-1.5 text-left min-h-[40px]">
                    <MapPin className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                    <span className="text-[12px] text-gray-700 font-medium truncate flex-1">{selectedHeroLocation}</span>
                    <svg className={'w-3 h-3 text-gray-400 shrink-0 transition-transform ' + (showDropdown ? 'rotate-180' : '')} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {showDropdown && (
                    <div
                      className="absolute top-full left-0 bg-white border border-gray-100 rounded-lg py-1 overflow-y-auto"
                      style={{ zIndex: 99999, marginTop: '4px', width: '200px', maxHeight: '220px', boxShadow: '0 10px 24px rgba(15,23,42,0.16)', fontFamily: "'Poppins', sans-serif" }}
                    >
                      {HERO_LOCATIONS.map((loc) => (
                        <button
                          key={loc}
                          onClick={() => { setSelectedHeroLocation(loc); setShowDropdown(false); }}
                          className="w-full text-left flex items-center transition-colors duration-150"
                          style={{
                            padding: '8px 12px',
                            fontSize: '13px',
                            fontWeight: selectedHeroLocation === loc ? 700 : 500,
                            color: selectedHeroLocation === loc ? '#ec4899' : '#111827',
                            background: selectedHeroLocation === loc ? '#fdf2f8' : 'transparent',
                            height: '36px',
                          }}
                          onMouseEnter={e => { if (selectedHeroLocation !== loc) (e.currentTarget as HTMLButtonElement).style.background = '#fdf2f8'; }}
                          onMouseLeave={e => { if (selectedHeroLocation !== loc) (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                        >
                          {loc}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button onClick={() => setShowDropdown(false)} className="bg-[#f97316] hover:bg-[#ea580c] text-white text-[13px] font-bold px-5 py-2.5 sm:py-0 transition-colors duration-200 shrink-0 min-h-[40px] sm:rounded-r-md w-full sm:w-auto">
                  <span className="flex items-center justify-center gap-1.5">
                    <Search className="w-3.5 h-3.5 sm:hidden" />
                    Search
                  </span>
                </button>
              </div>
            </div>

          </motion.div>
        </div>
      </section>
      <NewsTicker />

      {/* ── Filter Bar ── */}
      <div className="bg-white border-b border-gray-100">
        <div className="page-container pt-6 pb-4 space-y-4">

          {/* Explore by Dessert Type */}
          <div>
            <p className="text-[11px] font-bold text-gray-400 uppercase mb-2.5" style={{ fontFamily: "'Poppins', sans-serif", letterSpacing: '0.12em' }}>
              Explore by Type
            </p>
            <div className="flex gap-2 overflow-x-auto filter-scroll pb-1 flex-wrap">
              {DESSERT_TYPES.map((c) => {
                const active = selectedType === c.name;
                // 'Cafe' navigates to the Cafe category page instead of filtering.
                if ((c as any).href) {
                  return (
                    <button
                      key={c.name}
                      onClick={() => navigate((c as any).href)}
                      className="flex items-center gap-1.5 rounded-full border whitespace-nowrap transition-colors duration-150 shrink-0"
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        height: '35px',
                        padding: '0 13px',
                        fontSize: '12.5px',
                        fontWeight: 600,
                        background: '#ffffff',
                        color: '#111827',
                        borderColor: '#e5e7eb',
                        boxShadow: 'none',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#fffbeb';
                        e.currentTarget.style.borderColor = '#fcd34d';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#ffffff';
                        e.currentTarget.style.borderColor = '#e5e7eb';
                      }}
                    >
                      <span className="text-[14px] leading-none">{c.icon}</span>
                      {c.name}
                    </button>
                  );
                }
                return (
                  <button
                    key={c.name}
                    onClick={() => setSelectedType(active ? null : c.name)}
                    className="flex items-center gap-1.5 rounded-full border whitespace-nowrap transition-colors duration-150 shrink-0"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      height: '35px',
                      padding: '0 13px',
                      fontSize: '12.5px',
                      fontWeight: 600,
                      background: active ? '#ec4899' : '#ffffff',
                      color: active ? '#ffffff' : '#111827',
                      borderColor: active ? '#ec4899' : '#e5e7eb',
                      boxShadow: 'none',
                    }}
                    onMouseEnter={(e) => {
                      if (!active) {
                        e.currentTarget.style.background = '#fdf2f8';
                        e.currentTarget.style.borderColor = '#f9a8d4';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!active) {
                        e.currentTarget.style.background = '#ffffff';
                        e.currentTarget.style.borderColor = '#e5e7eb';
                      }
                    }}
                  >
                    <span className="text-[14px] leading-none">{c.icon}</span>
                    {c.name}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <div className="flex gap-2 overflow-x-auto filter-scroll pb-1 flex-wrap">
              <button
                onClick={() => { setSelectedType(null); setSelectedLocation(null); }}
                className="flex items-center gap-1.5 rounded-full border border-dashed whitespace-nowrap transition-colors duration-150 shrink-0"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  height: '35px',
                  padding: '0 13px',
                  fontSize: '12.5px',
                  fontWeight: 600,
                  color: '#6b7280',
                  background: '#ffffff',
                  borderColor: '#d1d5db',
                  boxShadow: 'none',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#ec4899';
                  e.currentTarget.style.borderColor = '#ec4899';
                  e.currentTarget.style.background = '#fdf2f8';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#6b7280';
                  e.currentTarget.style.borderColor = '#d1d5db';
                  e.currentTarget.style.background = '#ffffff';
                }}
              >
                <span className="text-[12px] leading-none">✕</span>
                Clear Filters
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Dessert Listings ── */}
      <section className="bg-gray-50/50 py-8">
        <div className="page-container">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
            <h1 className="text-[18px] sm:text-[22px] md:text-[26px] font-bold text-gray-900 mb-3" style={{ fontFamily: "'Lora', serif" }}>
              Top Indian &amp; South Asian Desserts, Bakeries &amp; Sweet Shops in Dallas–Fort Worth
            </h1>

            <p className="text-[12px] sm:text-[13px] text-gray-500 mb-6" style={{ fontFamily: "'Lora', serif" }}>
              Discover Indian, Pakistani, and South Asian desserts, bakeries, sweet shops, home bakers, and dessert spots across the Dallas–Fort Worth area with DallasDesiScoop. Find Indian sweets, mithai, fusion desserts, custom cakes, ice cream, milkshakes, pastries, cookies, brownies, cakesicles, waffles, dessert rolls, and specialty treats. Explore dessert shops and bakeries in Dallas, Frisco, Plano, Irving, McKinney, Allen, Carrollton, Richardson, Lewisville, Coppell, The Colony, Prosper, Flower Mound, Little Elm, and surrounding DFW communities.
            </p>

            {/* Dessert grid */}
            <AdminAddBar mc={mc} fields={DESSERT_FIELDS} label="Add Dessert Spot" />

            {/* Featured by TastebudsByAnubhi */}
            {featuredDesserts.length > 0 && (
              <div className="mb-8">
                <h3 className="text-[14px] sm:text-[16px] font-bold text-green-700 mb-3 flex items-center gap-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  Featured by TastebudsByAnubhi
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                  {featuredDesserts.map((d, i) => (
                    <AdminItem key={(d as any)._mcId} mc={mc} item={d as any} fields={DESSERT_FIELDS} title="Dessert Spot">
                      <DessertCard dessert={d as any} index={i} isFeatured />
                    </AdminItem>
                  ))}
                </div>
              </div>
            )}

            {/* More Dessert Shops */}
            {nonFeaturedDesserts.length > 0 && (
              <div>
                {featuredDesserts.length > 0 && (
                  <h3 className="text-[14px] sm:text-[16px] font-bold text-gray-700 mb-3" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    More Dessert Shops
                  </h3>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                  {nonFeaturedDesserts.map((d, i) => (
                    <AdminItem key={(d as any)._mcId} mc={mc} item={d as any} fields={DESSERT_FIELDS} title="Dessert Spot">
                      <DessertCard dessert={d as any} index={i} />
                    </AdminItem>
                  ))}
                </div>
              </div>
            )}

            {filteredDesserts.length === 0 && (
              <div className="text-center py-16">
                <div className="text-4xl mb-3">🍰</div>
                <p className="text-[15px] font-semibold text-gray-700 mb-1" style={{ fontFamily: "'Poppins', sans-serif" }}>No dessert spots found for this filter.</p>
                <p className="text-[12px] text-gray-400" style={{ fontFamily: "'Poppins', sans-serif" }}>Try a different type or location.</p>
                {hasActiveFilters && (
                  <button
                    onClick={() => { setSelectedType(null); setSelectedLocation(null); }}
                    className="mt-4 px-5 py-2 rounded-full bg-pink-50 border border-pink-200 text-pink-600 text-[12px] font-semibold hover:bg-pink-100 transition-colors"
                    style={{ fontFamily: "'Poppins', sans-serif" }}
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            )}

            <p className="text-[12px] sm:text-[13px] text-gray-500 mt-8" style={{ fontFamily: "'Lora', serif" }}>
              Looking for Indian desserts near you, Pakistani sweets, South Asian desserts, Indian sweet shops, Pakistani bakeries, mithai shops, custom cake makers, home bakers, or specialty dessert stores in DFW? DallasDesiScoop helps you discover local dessert businesses offering traditional Indian sweets, Pakistani desserts, Bengali sweets, fusion desserts, eggless cakes, birthday cakes, wedding cakes, celebration cakes, cupcakes, pastries, ice cream, kulfi, falooda, milkshakes, waffles, brownies, cookies, and other sweet treats. Whether you're searching for desserts for a celebration, party, wedding, birthday, festival, or simply satisfying your sweet tooth, explore dessert shops, bakeries, sweet shops, and home bakers throughout the Dallas–Fort Worth area.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
